import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import CloseIcon from '@common/components/icons/CloseIcon';
import PlusIcon from '@common/components/icons/PlusIcon';
import {
  SelectMulti,
  AsyncSelectMulti,
  SelectSingle,
} from '@common/components/SelectMulti';
import { useClickOutside } from '@common/hooks/useClickOutside';
import { useIsScrollable } from '@common/hooks/useIsScrollable';
import { createEmptyFilter } from '@common/utils/filterUtils';
import * as styles from './index.module.css';

/**
 * Check if a filter type is async (loads options from API)
 * Checks filterTypes prop for isAsync property
 * @param {string} filterType - The filter type value
 * @param {Array} filterTypes - Array of filter type definitions with isAsync property
 * @returns {boolean} - True if this filter type loads options asynchronously
 */
const isAsyncFilterType = (filterType, filterTypes = []) => {
  if (!filterType) return false;
  const type = filterTypes.find((t) => t.value === filterType);
  return type?.isAsync ?? false;
};

export function FilterDropdown({
  isOpen,
  onClose,
  filters = [],
  onFiltersChange,
  filterTypes = [],
  filterValues = {},
  loadFilterOptions,
  onOpen,
}) {
  const [localFilters, setLocalFilters] = useState(filters);
  const filterRowsRef = useRef(null);
  const isScrollable = useIsScrollable(filterRowsRef, [localFilters]);
  const hasLoadedOnOpen = useRef(false);

  const syncLocalFiltersWithProp = () => {
    setLocalFilters(filters.length > 0 ? filters : [createEmptyFilter()]);
  };

  useEffect(syncLocalFiltersWithProp, [filters]);

  // Load dynamic filter options when dropdown opens
  useEffect(() => {
    if (isOpen && !hasLoadedOnOpen.current && onOpen) {
      hasLoadedOnOpen.current = true;
      onOpen();
    }
    if (!isOpen) {
      hasLoadedOnOpen.current = false;
    }
  }, [isOpen, onOpen]);

  const handleClickOutside = useCallback(() => {
    onClose();
    setLocalFilters(filters.length > 0 ? filters : [createEmptyFilter()]);
  }, [onClose, filters]);

  const dropdownRef = useClickOutside(handleClickOutside, isOpen);

  const addFilterRow = () => {
    setLocalFilters([...localFilters, createEmptyFilter()]);
  };

  const removeFilterRow = (filterId) => {
    if (localFilters.length > 1) {
      setLocalFilters(localFilters.filter((filter) => filter.id !== filterId));
    }
  };

  const updateFilterType = (filterId, newType) => {
    // All filters use array values (for multi-select)
    setLocalFilters(
      localFilters.map((filter) =>
        filter.id === filterId
          ? { ...filter, type: newType, value: [] }
          : filter,
      ),
    );
  };

  const updateFilterValue = (filterId, newValue) => {
    setLocalFilters(
      localFilters.map((filter) =>
        filter.id === filterId ? { ...filter, value: newValue } : filter,
      ),
    );
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleDiscard = () => {
    setLocalFilters(filters.length > 0 ? filters : [createEmptyFilter()]);
    onClose();
  };

  // Convert filterTypes to react-select format
  const filterTypeOptions = useMemo(
    () =>
      filterTypes.map((type) => ({
        value: type.value,
        label: type.label,
      })),
    [filterTypes],
  );

  const renderValueSelect = (filter) => {
    const isAsync = isAsyncFilterType(filter.type, filterTypes);

    // If async and loadFilterOptions is provided, use AsyncSelectMulti
    if (isAsync && loadFilterOptions) {
      return (
        <AsyncSelectMulti
          value={Array.isArray(filter.value) ? filter.value : []}
          onChange={(newValue) => updateFilterValue(filter.id, newValue)}
          placeholder="Select..."
          isDisabled={!filter.type}
          loadOptions={loadFilterOptions}
          filterType={filter.type}
        />
      );
    }

    // Use static SelectMulti for non-async filters with predefined options
    return (
      <SelectMulti
        options={filterValues[filter.type] || []}
        value={Array.isArray(filter.value) ? filter.value : []}
        onChange={(newValue) => updateFilterValue(filter.id, newValue)}
        placeholder="Select..."
        isDisabled={!filter.type}
      />
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div className={styles['dropdown-header']}>Filters</div>

      <div className={styles['dropdown-body']}>
        <div className={styles['filter-rows-container']} ref={filterRowsRef}>
          {localFilters.map((filter) => (
            <div key={filter.id} className={styles['filter-row']}>
              <SelectSingle
                options={filterTypeOptions}
                value={filter.type}
                onChange={(newType) => updateFilterType(filter.id, newType)}
                placeholder="Select..."
                isSearchable
              />

              <div className={styles['filter-value-container']}>
                {renderValueSelect(filter)}
              </div>

              <button
                onClick={() => removeFilterRow(filter.id)}
                className={styles['remove-filter-btn']}
                type="button"
                disabled={localFilters.length === 1}
              >
                <CloseIcon />
              </button>
            </div>
          ))}
        </div>

        <div
          className={`${styles['add-filter-row']} ${isScrollable ? styles['add-filter-row-with-border'] : ''}`}
        >
          <button
            onClick={addFilterRow}
            className={styles['add-filter-btn']}
            type="button"
          >
            <PlusIcon />
            <span>Add filter</span>
          </button>
        </div>
      </div>

      <div className={styles['dropdown-footer']}>
        <button
          onClick={handleDiscard}
          className={styles['discard-btn']}
          type="button"
        >
          Discard
        </button>
        <button
          onClick={handleApply}
          className={styles['apply-btn']}
          type="button"
        >
          Apply
        </button>
      </div>
    </div>
  );
}
